import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight,
    ChevronLeft,
    RotateCcw,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Info,
    Mail,
    Loader2,
    Scale,
} from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import { submitInquiry } from "@/lib/api";

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

/* ---------------------------------------------------------------------- */
/*  QUESTION CONTENT — Section 20A ring-fencing of assessed losses         */
/* ---------------------------------------------------------------------- */

const THRESHOLD = {
    old: "R1,817,001 (this is the top of the highest, 45%, tax bracket)",
    new: "R695,801 (this is the entry point of the 39% tax bracket for the 2027 tax year)",
};

const TAX_YEAR_LABEL = {
    old: "2026 tax year (1 March 2025 – 28 February 2026) or an earlier year",
    new: "2027 tax year (1 March 2026 – 28 February 2027) or a later/current year",
};

function questionFor(id, answers) {
    switch (id) {
        case "q1":
            return {
                step: 1,
                title: "Which tax year are we checking?",
                helper:
                    "We ask this FIRST because it genuinely changes the answer. From 1 March 2026, the income level at which these ring-fencing rules can apply was lowered — from the old top 45% tax bracket down to the 39% bracket. That means quite a few more taxpayers now fall into the danger zone than before. Please pick the tax year the sideline loss actually relates to.",
                options: [
                    { label: "The 2026 tax year, or any earlier tax year (ending on or before 28 February 2026)", value: "old" },
                    { label: "The 2027 tax year, or a later tax year — this includes the CURRENT tax year, which started on 1 March 2026", value: "new" },
                ],
            };
        case "q2":
            return {
                step: 2,
                title: "Is your sideline trade a farming or animal-breeding activity of any kind?",
                helper:
                    "Think crops, livestock, game, poultry, or any other agricultural activity. Farming is treated quite differently to every other type of sideline trade under these rules — sometimes better, sometimes worse — so we need to know this upfront before asking anything else.",
                options: [
                    { label: "No — my sideline trade has nothing to do with farming or animal breeding", value: "none" },
                    { label: "Yes — and it's run full-time, on a genuine, commercial basis (not a weekend or spare-time activity)", value: "fulltime" },
                    { label: "Yes — but it's more of a part-time, weekend, or hobby-type farming or animal-breeding activity", value: "parttime" },
                ],
            };
        case "q3":
            return {
                step: 3,
                title: "Besides farming, does your sideline trade match any of the following suspect-trade categories?",
                helper:
                    "SARS treats a specific, limited list of trades with extra suspicion because they are commonly used as \"tax-loss hobbies\" rather than genuine businesses. Some common examples: playing sport for reward; dealing in collectables such as fine art, coins, stamps, or wine; letting out a boat, aircraft or vehicle that you or a close relative also uses privately for more than just a small part of the time; showing or breeding animals as a hobby; creative arts or performances (music, acting, and similar) that you personally carry out; and speculative trading in crypto-assets. (We ask about letting residential property to a relative separately, in the next question.) If your sideline trade genuinely matches one of these, select Yes.",
                options: [
                    { label: "Yes — my sideline trade matches one (or more) of these categories", value: "yes" },
                    { label: "No — my sideline trade doesn't match any of these categories", value: "no" },
                ],
            };
        case "q4":
            return {
                step: 4,
                title: "Do you let residential property — a flat, cottage, or house — to a relative or family member?",
                helper:
                    "This is its own specific \"suspect trade\" category under Section 20A, separate from the general list in the previous question. It applies where you rent out residential accommodation and a relative (or other connected person) is the tenant or otherwise benefits from living there, unless at least 80% of that accommodation is used by people who are NOT your relatives for at least half of the tax year. Family arrangements — like renting a cottage to your adult child at a low rent — are exactly the kind of situation this rule was designed to catch.",
                options: [
                    { label: "Yes — I let residential property to a relative or family member", value: "yes" },
                    { label: "No — I don't let residential property to a relative or family member", value: "no" },
                ],
            };
        case "q5": {
            const threshold = THRESHOLD[answers.taxYear] || THRESHOLD.old;
            return {
                step: 5,
                title: `Leaving this sideline loss out of the picture completely, was your OTHER taxable income for the year — your salary, your other business profits, rental or investment income, and anything else — equal to or more than ${threshold}?`,
                helper:
                    "This is the single most important gatekeeper question. Section 20A of the South African Income Tax Act, can only ever apply to people whose OTHER income already puts them in the top tax bracket. If your other income sits below this level, the loss can NEVER be ring-fenced, no matter what kind of sideline trade it is or how many years it has lost money — you can stop right here and deduct the loss normally.",
                options: [
                    { label: "Yes — my other taxable income for the year was at or above that amount", value: "yes" },
                    { label: "No — my other taxable income for the year was below that amount", value: "no" },
                ],
            };
        }
        case "q6":
            return {
                step: 6,
                title: "Think about the last 5 tax years, including the current one. In how many of those years did THIS SAME sideline trade or business end up making a tax LOSS (expenses more than income) instead of a profit?",
                helper:
                    "Don't worry about doing any maths here — just count honestly. A trade that has lost money in at least 3 of the last 5 years is automatically treated as \"suspect\" too, even if it isn't on the specific list in the earlier questions.",
                options: [0, 1, 2, 3, 4, 5].map((n) => ({
                    label: n === 0 ? "0 years — it hasn't made a loss recently" : `${n} year${n > 1 ? "s" : ""}`,
                    value: n,
                })),
            };
        case "q7":
            return {
                step: 7,
                title: "Now go back another 5 years before that (so, broadly, 6 to 10 years ago). In how many of THOSE years did the same trade or business also make a loss?",
                helper:
                    "We're building up to the full 10-year picture in two easy 5-year chunks so you don't have to recount everything from scratch. Again, just count honestly — an estimate is fine if records from that far back are incomplete.",
                options: [0, 1, 2, 3, 4, 5].map((n) => ({
                    label: n === 0 ? "0 years" : `${n} year${n > 1 ? "s" : ""}`,
                    value: n,
                })),
            };
        default:
            return null;
    }
}

/* ---------------------------------------------------------------------- */
/*  TRANSITION LOGIC — decides the next question id or a terminal outcome  */
/* ---------------------------------------------------------------------- */

function advance(currentId, value, answers) {
    switch (currentId) {
        case "q1":
            return { next: "q2" };
        case "q2":
            return { next: answers.farmingType === "parttime" ? "q5" : "q3" };
        case "q3":
            return { next: "q4" };
        case "q4":
            return { next: "q5" };
        case "q5":
            if (value === "no") return { outcome: "not_ringfenced_income" };
            return { next: "q6" };
        case "q6": {
            const suspect = answers.farmingType === "parttime" || answers.suspectTrade === "yes" || answers.residentialLet === "yes";
            const triggered = suspect || value >= 3;
            if (!triggered) return { outcome: "not_ringfenced_trigger" };
            return { next: "q7" };
        }
        case "q7": {
            const total10 = (answers.lossYears5 || 0) + value;
            const exemptFarming = answers.farmingType === "fulltime";
            if (total10 >= 6 && !exemptFarming) return { outcome: "ringfenced_permanent" };
            return { outcome: "ringfenced_provisional" };
        }
        default:
            return { outcome: "not_ringfenced_trigger" };
    }
}

const FIELD_BY_ID = {
    q1: "taxYear",
    q2: "farmingType",
    q3: "suspectTrade",
    q4: "residentialLet",
    q5: "ownIncome",
    q6: "lossYears5",
    q7: "lossYearsPrior5",
};

/* ---------------------------------------------------------------------- */
/*  OUTCOME CONTENT                                                       */
/* ---------------------------------------------------------------------- */

const OUTCOMES = {
    not_ringfenced_income: {
        kind: "good",
        label: "Not ring-fenced",
        title: "Good news — this loss will NOT be ring-fenced",
        summary: (a) => [
            `Section 20A can only apply once a person's OTHER taxable income (everything except this sideline loss) reaches ${
                a.taxYear === "old" ? "R1,817,001" : "R695,801"
            } or more for the ${TAX_YEAR_LABEL[a.taxYear] || TAX_YEAR_LABEL.old}.`,
            `Because your other income sits below that level, none of the "suspect trade" rules, the 3-out-of-5-years test, or the 6-out-of-10-years catch-all can apply to you at all — it genuinely does not matter what kind of sideline trade this is, or how many years it has made a loss.`,
            `You can deduct this year's loss against your other income in the ordinary way, exactly as Section 20(1)(b) of the Income Tax Act normally allows.`,
        ],
    },
    not_ringfenced_trigger: {
        kind: "good",
        label: "Not ring-fenced",
        title: "Good news — this loss will NOT be ring-fenced",
        summary: (a) => [
            `Your other taxable income for the ${TAX_YEAR_LABEL[a.taxYear] || TAX_YEAR_LABEL.old} does fall at or above the top-bracket threshold, so you clear the first gate.`,
            `However, ring-fencing only actually bites once your trade is either one of SARS's specifically listed "suspect trades" OR has produced a loss in 3 or more of the last 5 tax years — and based on your answers, neither of those is the case here (you told us the trade doesn't match the suspect-trade categories, and it has made a loss in fewer than 3 of the last 5 years).`,
            `Because neither trigger is met, Section 20A simply doesn't switch on for this trade. You can deduct the loss against your other income normally.`,
        ],
    },
    ringfenced_permanent: {
        kind: "bad",
        label: "Ring-fenced — no escape clause available",
        title: "This loss WILL be ring-fenced, and there is no way to argue around it this year",
        summary: (a) => [
            `Your other income clears the top-bracket threshold, AND your trade either matches a suspect-trade category or has lost money in 3 or more of the last 5 years — so the ring-fencing rules are switched on.`,
            `Normally, at this point, you would still get one last chance: the "reasonable prospect of profit" escape clause, where you can show SARS that the trade is genuinely being run as a business with a realistic path to profit.`,
            `Unfortunately, because this same trade has now produced a loss in 6 or more of the last 10 tax years, the legislation applies a permanent "catch-all" rule that switches the escape clause off completely (this catch-all does not apply to genuine full-time farming operations, which is why we asked about that earlier).`,
            `In practice, this means the loss cannot reduce your other taxable income this year — it is carried forward and can only be used against future profits from this exact same trade.`,
        ],
    },
    ringfenced_provisional: {
        kind: "warn",
        label: "Provisionally ring-fenced — an escape route may still be open",
        title: "This loss is provisionally ring-fenced, but you may still have a way out",
        summary: (a) => [
            `Your other income clears the top-bracket threshold, AND your trade either matches a suspect-trade category or has lost money in 3 or more of the last 5 years — so, on the face of it, the ring-fencing rules apply.`,
            `The good news is that the permanent 10-year "catch-all" rule does not apply to you${
                a.farmingType === "fulltime" ? " (because this is a genuine full-time farming operation, which is specifically excluded from that catch-all)" : " (because this trade hasn't lost money in 6 or more of the last 10 years)"
            }. That means you still have access to the "reasonable prospect of profit" escape clause in Section 20A(3).`,
            `SARS will weigh up six things holistically: how your income compares to your expenses; how much you spend on advertising and finding customers; whether the trade is run in a genuinely businesslike way (staff, premises, equipment, proper records); whether any loss years were caused by a one-off, unusual event; whether you have a credible, documented plan to reach profitability; and how much you personally use the trade's assets for your own enjoyment.`,
            `This is a judgement call based on your specific facts, not a simple checklist — which is exactly why we'd recommend a short consultation before you file, so we can build the strongest possible case with you.`,
        ],
    },
};

const RINGFENCING_EXPLAINER_PARAGRAPHS = [
    "Ring-fencing does NOT mean you lose your loss forever, and it does not mean you are being fined or penalised. All it means is a change in TIMING and WHERE the loss can be used.",
    "Normally, if a sideline trade makes a loss, you can deduct that loss from your other income — your salary, your main business profit, your investments — and pay less tax overall this year.",
    "When a loss is \"ring-fenced\" under Section 20A of the Income Tax Act, SARS draws a fence around it: the loss can no longer reduce your OTHER income this year. Instead, it is carried forward, year after year, and sits waiting until that exact same sideline trade eventually turns a profit — at which point the banked-up losses are used to reduce the tax on THAT future profit, and only that.",
    "The rule exists because, without it, someone could run a permanently loss-making hobby (breeding horses, dealing in art, renting a holiday home to family, and so on) purely to shelter a large salary from tax, with no real intention of ever making that hobby profitable.",
    "Whether ring-fencing applies to you specifically depends on three things: how high your OTHER income is, whether your trade is on a short list of \"suspect\" activities (or has a poor loss history), and — if it is caught — whether you can prove to SARS that there is a genuine, reasonable prospect of the trade becoming profitable within a reasonable time.",
];

/* ---------------------------------------------------------------------- */
/*  MAIN COMPONENT                                                        */
/* ---------------------------------------------------------------------- */

export default function RingFencing() {
    const [stepId, setStepId] = useState("q1");
    const [answers, setAnswers] = useState({});
    const [history, setHistory] = useState([]); // [{ stepId, label, value }]
    const [outcome, setOutcome] = useState(null);

    const current = outcome ? null : questionFor(stepId, answers);
    const totalSteps = 7;

    const choose = (opt) => {
        const field = FIELD_BY_ID[stepId];
        const nextAnswers = { ...answers, [field]: opt.value };
        const nextHistory = [...history, { stepId, question: current.title, label: opt.label }];
        setAnswers(nextAnswers);
        setHistory(nextHistory);

        const result = advance(stepId, opt.value, nextAnswers);
        if (result.outcome) {
            setOutcome(result.outcome);
        } else {
            setStepId(result.next);
        }
    };

    const goBack = () => {
        if (history.length === 0) return;
        const prevHistory = history.slice(0, -1);
        const last = history[history.length - 1];
        const field = FIELD_BY_ID[last.stepId];
        const prevAnswers = { ...answers };
        delete prevAnswers[field];
        setHistory(prevHistory);
        setAnswers(prevAnswers);
        setStepId(last.stepId);
        setOutcome(null);
    };

    const reset = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStepId("q1");
        setAnswers({});
        setHistory([]);
        setOutcome(null);
    };

    const pct = Math.min(100, Math.round((history.length / totalSteps) * 100));

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-ringfencing">
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link to="/checklists" className="btn-ghost text-base gap-2.5" data-testid="back-to-checklists">
                        <ChevronLeft className="w-5 h-5" /> All checklists
                    </Link>
                    <button onClick={reset} className="btn-ghost" data-testid="ringfencing-restart">
                        <RotateCcw className="w-4 h-4" /> Restart
                    </button>
                </div>
            </div>

            <section className="container-tca py-12 md:py-16">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-8">
                        <p className="overline mb-3">Section 20A of the Income Tax Act</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl leading-tight">
                            Assessed Losses: Ring-Fencing Checklist for Sideline Hustles &amp; Businesses
                        </h1>
                        <p className="mt-4 text-tca-mute leading-relaxed">
                            Running a side hustle or small business alongside your main job or profession, and it's
                            making a loss? Find out — in plain English — whether SARS will let you deduct that loss
                            against your other income, or whether it gets "ring-fenced."
                        </p>
                    </div>

                    <div className="mb-8 h-1 bg-tca-line w-full" data-testid="ringfencing-progress">
                        <div className="h-full bg-tca-orange transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>

                    <div className="bg-white border border-tca-line p-7 sm:p-10">
                        {!outcome && current && (
                            <div className="space-y-7" data-testid={`ringfencing-question-${current.step}`}>
                                <div>
                                    <p className="overline text-tca-orange mb-3">Question {current.step} of {totalSteps}</p>
                                    <h2 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight">
                                        {current.title}
                                    </h2>
                                    {current.helper && (
                                        <p className="mt-3 text-sm text-tca-mute leading-relaxed flex items-start gap-2">
                                            <Info className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" /> {current.helper}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2.5">
                                    {current.options.map((o, i) => (
                                        <button
                                            key={i}
                                            onClick={() => choose(o)}
                                            data-testid={`ringfencing-option-${current.step}-${i}`}
                                            className="w-full text-left bg-white border border-tca-line hover:border-tca-orange hover:bg-tca-orangeSoft/50 transition-all px-5 py-4 group flex items-center justify-between gap-3"
                                        >
                                            <span className="font-medium text-tca-ink">{o.label}</span>
                                            <ArrowRight className="w-4 h-4 text-tca-mute group-hover:text-tca-orange transition-colors shrink-0 ml-3" />
                                        </button>
                                    ))}
                                </div>
                                {history.length > 0 && (
                                    <button onClick={goBack} data-testid={`ringfencing-back-${current.step}`} className="btn-ghost">
                                        <ChevronLeft className="w-4 h-4" /> Back
                                    </button>
                                )}
                            </div>
                        )}

                        {outcome && (
                            <RingFencingResult
                                outcomeKey={outcome}
                                answers={answers}
                                history={history}
                                onReset={reset}
                            />
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/*  RESULT VIEW — verdict card + ALWAYS-shown explainer + lead form         */
/* ---------------------------------------------------------------------- */

function RingFencingResult({ outcomeKey, answers, history, onReset }) {
    const outcome = OUTCOMES[outcomeKey];
    const summaryParagraphs = outcome.summary(answers);
    const summaryText = summaryParagraphs.join(" ");
    const map = {
        good: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-300" },
        bad: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-300" },
        warn: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-300" },
    };
    const style = map[outcome.kind];
    const Icon = style.icon;

    return (
        <div data-testid="ringfencing-result">
            {/* Verdict */}
            <div className={`flex items-start gap-4 p-5 ${style.bg} border ${style.border} mb-6`}>
                <Icon className={`w-7 h-7 ${style.color} shrink-0 mt-0.5`} />
                <div className="flex-1">
                    <p className={`overline ${style.color} mb-1.5`}>{outcome.label}</p>
                    <h3 className="font-display font-extrabold text-tca-ink text-xl mb-2">{outcome.title}</h3>
                    <div className="space-y-2.5">
                        {summaryParagraphs.map((p, i) => (
                            <p key={i} className="text-sm text-tca-ink leading-relaxed">{p}</p>
                        ))}
                    </div>
                </div>
            </div>

            {/* ALWAYS-shown green explainer — regardless of outcome */}
            <div className="bg-emerald-50 border-2 border-emerald-500 p-5 mb-6" data-testid="ringfencing-explainer">
                <div className="flex items-start gap-3">
                    <Scale className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                    <div className="space-y-2.5">
                        <p className="overline text-emerald-800 mb-0">What does "ring-fencing" actually mean?</p>
                        {RINGFENCING_EXPLAINER_PARAGRAPHS.map((p, i) => (
                            <p key={i} className="text-sm text-emerald-900 leading-relaxed">{p}</p>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-tca-orangeSoft/40 border border-tca-orange p-5 mb-6">
                <p className="font-display font-bold text-tca-ink mb-1">Want certainty on your specific facts?</p>
                <p className="text-sm text-tca-ink leading-relaxed mb-4">
                    This tool answers the general principle. Whether SARS agrees with a "reasonable prospect of profit"
                    argument — or whether a trade truly counts as a suspect trade — depends on the fine detail. Book a
                    30-minute virtual meeting and a CA(SA) will run it with you before you file.
                </p>
                <CTAButtons testIdPrefix="ringfencing-result-cta" />
            </div>

            <RingFencingLeadForm outcomeKey={outcomeKey} outcome={outcome} answers={answers} history={history} summaryText={summaryText} />

            <button onClick={onReset} className="btn-ghost mt-6" data-testid="ringfencing-take-again">
                <RotateCcw className="w-4 h-4" /> Take it again
            </button>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/*  LEAD CAPTURE — emails the result + branded PDF, mirrors other tools    */
/* ---------------------------------------------------------------------- */

function RingFencingLeadForm({ outcomeKey, outcome, answers, history, summaryText }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const emailTrim = form.email.trim();
    const nameTrim = form.name.trim();
    const emailValid = EMAIL_RE.test(emailTrim);
    const nameValid = nameTrim.length >= 2;
    const showEmailError = emailTrim.length > 0 && !emailValid;
    const canSubmit = nameValid && emailValid && status !== "loading";

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!nameValid) er.name = "Please enter your full name.";
        if (!emailValid) er.email = "Please enter a valid email address.";
        return er;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
                subject: `Ring-Fencing Checklist — ${outcome.label}`,
                message: `The Assessed Losses (Section 20A) Ring-Fencing Checklist returned "${outcome.label}" for ${form.name.trim()}. Answers: ${history
                    .map((h) => `${h.question} → ${h.label}`)
                    .join(" | ")}.`,
                source: "ringfencing",
                ringfencing: {
                    outcome_key: outcomeKey,
                    outcome_label: outcome.title,
                    tax_year_label: TAX_YEAR_LABEL[answers.taxYear] || TAX_YEAR_LABEL.old,
                    threshold_label: THRESHOLD[answers.taxYear] || THRESHOLD.old,
                    summary: summaryText,
                    answers: history.map((h) => ({ question: h.question, answer: String(h.label) })),
                },
            });
            setStatus("success");
            toast.success("Your ring-fencing result is on its way — check your inbox.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try again or contact us directly.");
        }
    };

    if (status === "success") {
        return (
            <div data-testid="ringfencing-lead-success" className="bg-white border border-tca-orange p-7 md:p-9 text-center">
                <div className="w-12 h-12 mx-auto bg-tca-orange/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-tca-orange" />
                </div>
                <h3 className="font-display font-extrabold text-tca-ink text-2xl mb-2">
                    Thanks {form.name.split(" ")[0]} — your result is on its way.
                </h3>
                <p className="text-tca-mute leading-relaxed max-w-lg mx-auto">
                    We've emailed your ring-fencing result (with a branded PDF) to {form.email}. A TC Auditors advisor
                    will follow up within one business day if you'd like help confirming it.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} data-testid="ringfencing-lead-form" className="bg-white border border-tca-line p-7 md:p-9" noValidate>
            <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-tca-orange/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-tca-orange" />
                </div>
                <div>
                    <h3 className="font-display font-extrabold text-tca-ink text-2xl leading-tight">Email me this result</h3>
                    <p className="text-sm text-tca-mute mt-1">
                        We'll send you this result plus a branded PDF, and a TC Auditors advisor will follow up. No
                        spam, no commitment.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Full name <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="ringfencing-lead-name"
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors ${errors.name ? "border-red-500" : "border-tca-line"}`}
                        placeholder="Jane Smith"
                    />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Email <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="ringfencing-lead-email"
                        aria-invalid={showEmailError || Boolean(errors.email)}
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none transition-colors ${
                            showEmailError || errors.email ? "border-red-500 bg-red-50 focus:border-red-500" : "border-tca-line focus:border-tca-orange"
                        }`}
                        placeholder="jane@company.co.za"
                    />
                    {(showEmailError || errors.email) && (
                        <p className="text-xs text-red-600 mt-1" data-testid="ringfencing-lead-email-error">
                            {errors.email || "Please enter a valid email address."}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Phone <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="ringfencing-lead-phone"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="079 461 7545"
                    />
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Company <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="ringfencing-lead-company"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="Acme (Pty) Ltd"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={!canSubmit}
                aria-disabled={!canSubmit}
                data-testid="ringfencing-lead-submit"
                className="btn-primary group mt-6 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                ) : (
                    <>
                        Email Me My Ring-Fencing Result
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                )}
            </button>

            <p className="text-[11px] text-tca-mute mt-4">
                By submitting, you consent to TC Auditors contacting you about your inquiry. We will never share your
                details.
            </p>
        </form>
    );
}
